import {
  Button,
  Descriptions,
  ConfirmationModal,
  EmptyState,
  FormCard,
  NoDataIcon,
  PageHeader,
  Trash,
  Warning,
  theme,
} from '@lmc/ui-kit'
import { isPlanOverdue, scrollToTop } from '../../utils/utils'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ProfileWrapper,
  ButtonsWrapper,
  ProfileContent,
} from '../../styledComponents'
import { useEffect, useRef, useState } from 'react'
import { Form as AntdForm, Divider, Tag } from 'antd'
import ContingencyPlanForm from './components/ContingencyPlanForm'
import useContingencyPlan from 'hooks/useContingencyPlan'
import { Spinner } from 'components/generic'
import { getNominative, showToast } from 'common/utils'
import styled from 'styled-components'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { generalConstants } from 'locale/general'
import {
  getContingencyPlanOtherFields,
  getContingencyPlanScenarioFields,
} from './getContingencyPlanFields'
import GenericConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import NavigationModal from 'components/NavigationModal/NavigationModal'

const ViewEditContingencyPlan = () => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { t: generalTranslations } = useTranslation(NAMESPACE.GENERAL)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [isReviewing, setIsReviewing] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const { contingencyPlanID } = useParams()
  const {
    contingencyPlan,
    deleteContingencyPlan,
    isDeleteLoading,
    isContingencyPlanLoading,
  } = useContingencyPlan({
    contingencyPlanID,
    onDeleteSuccess: () => {
      showToast({
        title: t(supConstants.CONTINGENCY_PLAN_DELETED),
      })
      navigate(`../`, { relative: 'path' })
    },
  })
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const planName = contingencyPlan?.planName

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isContingencyPlanLoading) {
    return <Spinner />
  }

  if (!contingencyPlan) {
    return <EmptyState title={t(supConstants.NO_PLAN)} icon={<NoDataIcon />} />
  }

  return (
    <ProfileWrapper ref={topRef}>
      <PageHeader
        backButtonHref="../"
        sticky
        childrenTitle={
          <TitleContainer>
            <StyledTitle level={1}>
              {planName || t(supConstants.CONTINGENCY_PLAN)}
            </StyledTitle>
            {isPlanOverdue(contingencyPlan.nextReviewDate) ? (
              <Tag color="red">{t(supConstants.OVERDUE)}</Tag>
            ) : null}
          </TitleContainer>
        }
      >
        {!isReviewing ? (
          <Button variant="primary" onClick={() => setIsReviewing(true)}>
            {t(supConstants.REVIEW)}
          </Button>
        ) : (
          <ButtonsWrapper>
            <ConfirmationModal
              localisation={{
                confirmText: generalTranslations(generalConstants.DELETE),
                cancelText: generalTranslations(generalConstants.CANCEL),
              }}
              modalContent={
                <ConfirmationModalContent>
                  <ConfirmationModalTitleContainer>
                    <Warning color={theme.color.status.warning} size={24} />
                    <Heading level={4}>
                      {t(supConstants.DELETE_PLAN_CONFIRMATION)}
                    </Heading>
                  </ConfirmationModalTitleContainer>
                  <Divider />
                  <Text> {t(supConstants.DELETE_PLAN_INFO)}</Text>
                  <Divider />
                </ConfirmationModalContent>
              }
            >
              <Button
                variant="danger"
                isLoading={isDeleteLoading}
                onClick={() => deleteContingencyPlan()}
              >
                <Trash />
                {t(supConstants.DELETE)}
              </Button>
            </ConfirmationModal>
            <GenericConfirmationModal shouldPrompt={isDirty}>
              <Button
                onClick={() => {
                  setIsDirty(false)
                  setIsReviewing(false)
                }}
              >
                {t(supConstants.CANCEL)}
              </Button>
            </GenericConfirmationModal>
            <Button
              variant="primary"
              onClick={form.submit}
              type="submit"
              isLoading={isUpdateLoading}
            >
              {t(supConstants.SAVE)}
            </Button>
          </ButtonsWrapper>
        )}
      </PageHeader>
      <ProfileContent>
        {isReviewing ? (
          <>
            <NavigationModal canNavigate={!isDirty} />
            <ContingencyPlanForm
              form={form}
              plan={contingencyPlan}
              setIsUpdateLoading={setIsUpdateLoading}
              setIsDirty={setIsDirty}
            />
          </>
        ) : (
          <>
            {contingencyPlan?.scenarios?.map((scenario, i) => (
              <FormCard title={`${t(supConstants.SCENARIO)} ${i + 1}`}>
                <Descriptions
                  labelStyle={labelStyle}
                  items={getContingencyPlanScenarioFields(
                    scenario,
                    residentNominative,
                    t
                  )}
                  bordered
                  column={1}
                />
              </FormCard>
            ))}
            <FormCard title={t(supConstants.OTHER_DETAILS)}>
              <Descriptions
                labelStyle={labelStyle}
                items={getContingencyPlanOtherFields(
                  contingencyPlan,
                  residentNominative,
                  t
                )}
                bordered
                column={1}
              />
            </FormCard>
          </>
        )}
      </ProfileContent>
    </ProfileWrapper>
  )
}

const labelStyle = { width: 272 }

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledTitle = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const ConfirmationModalContent = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.neutral.color3};
`

const ConfirmationModalTitleContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

export default ViewEditContingencyPlan
